import { runtimeConfig } from '@/shared/config'

// Зон здесь две, и путать их нельзя (канон tzkit §4). МОМЕНТ — «когда начнётся»
// — принадлежит человеку и читается в зоне его устройства: одно и то же мгновение
// коллеге в Москве и коллеге в Челябинске показывается разными цифрами, и это
// верно. КАЛЕНДАРНЫЕ СУТКИ — период отчёта, «сегодня» — принадлежат организации
// и считаются в APP_TZ: границы обязаны быть общими для всех. Момент на проводе
// всегда RFC3339 в UTC. Зона — параметр с дефолтом (момент — momentZone, сутки —
// appTimeZone), а не константа модуля: так функции тестируются без подмены
// окружения.

const MS_MINUTE = 60_000
const MS_HOUR = 3_600_000

export function appTimeZone(): string {
  return runtimeConfig.APP_TZ
}

export function deviceTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

// Зона момента — зона устройства смотрящего, и это вся логика: держателя нет,
// сервер о ней не спрашивается. Люди летают, а устройство едет за человеком само
// и без единого действия; сохранённая где-либо зона протухает молча. APP_TZ —
// запасная на среду, где Intl зону не назвал.
export function momentZone(): string {
  return deviceTimeZone() || appTimeZone()
}

// Зона устройства разошлась с зоной организации — повод один раз спокойно
// сказать, что календарные сутки считаются не по часам смотрящего. Сравниваются
// имена зон, а не смещения: равный офсет сегодня (Стамбул, Минск) ничего не
// гарантирует зимой, а эвристика по числу офсета — ровно тот источник вранья,
// от которого уходим.
export function deviceZoneDiffers(tz: string = appTimeZone()): boolean {
  const device = deviceTimeZone()
  return device !== '' && device !== tz
}

interface ZoneParts {
  year: number
  month: number
  day: number
  hour: number
  minute: number
}

// Календарные поля момента в зоне tz. Единственный способ спросить «какое там
// сейчас число»: getFullYear/getHours отвечают про зону устройства.
export function zoneParts(at: Date, tz: string = appTimeZone()): ZoneParts {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(at)
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value ?? '0')
  // hourCycle h23 отдаёт полночь как «24» в части локалей — нормализуем.
  const hour = value('hour') % 24
  return {
    year: value('year'),
    month: value('month'),
    day: value('day'),
    hour,
    minute: value('minute'),
  }
}

// Смещение зоны от UTC в конкретный момент, в минутах. Именно в момент: у зон с
// переводом стрелок оно зависит от даты.
export function zoneOffsetMinutes(at: Date, tz: string = appTimeZone()): number {
  const name = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'longOffset' })
    .formatToParts(at)
    .find((p) => p.type === 'timeZoneName')?.value
  const parsed = /^GMT([+-])(\d{2}):(\d{2})$/.exec(name ?? '')
  if (!parsed) return 0 // «GMT» без смещения — UTC
  const sign = parsed[1] === '-' ? -1 : 1
  return sign * (Number(parsed[2]) * 60 + Number(parsed[3]))
}

// «UTC+3» / «UTC−7» — смещение зоны на момент at (у зон с DST оно сезонное).
export function zoneOffsetLabel(at: Date = new Date(), tz: string = appTimeZone()): string {
  const minutes = zoneOffsetMinutes(at, tz)
  if (minutes === 0) return 'UTC'
  const sign = minutes < 0 ? '−' : '+'
  const abs = Math.abs(minutes)
  const hours = Math.floor(abs / 60)
  const rest = abs % 60
  return `UTC${sign}${hours}${rest ? `:${String(rest).padStart(2, '0')}` : ''}`
}

// Короткая подпись зоны («Москва»): берётся у Intl по имени зоны, а не
// хардкодится — APP_TZ может быть любой.
export function zoneTag(tz: string = appTimeZone(), at: Date = new Date()): string {
  // longGeneric, а не shortGeneric: короткое имя в Chromium не локализовано и
  // даёт «Moscow» латиницей посреди русского интерфейса. В Node оно как раз
  // локализовано, поэтому в юнит-тестах расхождение невидимо — проверять только
  // в браузере. Длинное имя локализовано в обоих движках, а его первая часть до
  // запятой и есть короткое: «Москва, стандартное время» → «Москва».
  const name = new Intl.DateTimeFormat('ru-RU', { timeZone: tz, timeZoneName: 'longGeneric' })
    .formatToParts(at)
    .find((p) => p.type === 'timeZoneName')?.value
  const short = name?.split(',')[0]?.trim()
  // Для зон без человеческого имени Intl отдаёт то же смещение — оно и подпись.
  return short && !/^(GMT|UTC)/.test(short) ? short : zoneOffsetLabel(at, tz)
}

// «Москва · UTC+3» — подпись зоны для бейджа: имя и смещение сразу.
export function zoneLabel(tz: string = appTimeZone(), at: Date = new Date()): string {
  const tag = zoneTag(tz, at)
  const offset = zoneOffsetLabel(at, tz)
  return tag === offset ? offset : `${tag} · ${offset}`
}

// Полная фраза о зоне показа момента — одна на весь интерфейс: ею подписывается
// любой показ момента (подсказка у тега зоны, у бейджа в шапке, у строки
// времени). Короткий тег рядом со значением отвечает «в какой зоне», фраза —
// «почему в этой». Зона организации называется только когда разошлась с зоной
// устройства: по ней считаются сутки отчётов, и смотрящий должен знать обе.
export function zoneHint(tz: string = momentZone()): string {
  const org = appTimeZone()
  const base = `Время показано в зоне вашего устройства (${tz})`
  return tz === org ? base : `${base}. Зона организации — ${org}.`
}

// Фраза о календарных сутках: границы периода — дни зоны организации, а не
// момент. Без неё цифры отчёта необъяснимы смотрящему из другой зоны.
export function zoneDayHint(tz: string = appTimeZone()): string {
  return `Сутки считаются по календарю зоны организации (${tz})`
}

// Ключ календарного дня момента в зоне tz («2026-07-08»): им сравнивают «тот же
// день» глазами смотрящего — «сегодня в 23:00» и «завтра в 01:00» соседние
// строки списка, только если так их видит он сам.
export function dayKey(iso: string, tz: string = momentZone()): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const p = zoneParts(d, tz)
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`
}

// «8 июля в 14:30» в зоне смотрящего; год добавляется, только если отличается от
// текущего (год тоже читается в этой зоне).
export function formatMeetingStart(
  iso: string,
  now: Date = new Date(),
  tz: string = momentZone(),
): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const sameYear = zoneParts(d, tz).year === zoneParts(now, tz).year
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: tz,
    day: 'numeric',
    month: 'long',
    ...(sameYear ? {} : { year: 'numeric' }),
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

// «14:30» в зоне смотрящего — конец интервала, когда дату уже несёт его начало.
export function formatTime(iso: string, tz: string = momentZone()): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

// Компактная ссылка на момент рядом с опорным: тот же день глазами смотрящего —
// только время («14:00»), иначе полная дата. Для «запланирована на …» в строке,
// где дату уже несёт фактическое начало.
export function formatTimeNear(iso: string, refIso: string, tz: string = momentZone()): string {
  const key = dayKey(iso, tz)
  if (key === '') return ''
  return key === dayKey(refIso, tz) ? formatTime(iso, tz) : formatMeetingStart(iso, new Date(), tz)
}

// «2 ч 05 мин» / «45 мин» / «меньше минуты»; undefined — если границы невалидны
// или конец раньше начала. Обе границы здесь серверные, и перевёрнутый интервал
// означает испорченные данные — их не показываем. Для «идёт столько-то», где
// верхняя граница берётся с часов клиента, есть formatElapsedSince.
export function formatDurationBetween(startIso: string, endIso: string): string | undefined {
  const start = new Date(startIso).getTime()
  const end = new Date(endIso).getTime()
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return undefined
  return formatDurationMs(end - start)
}

// «идёт 45 мин»: сколько прошло с начала по часам клиента. Отставшие на минуты
// часы браузера дают отрицательный интервал — это рассинхрон, а не порча данных,
// поэтому он схлопывается в «меньше минуты», и строка «идёт» никогда не остаётся
// без длительности. Невалидное начало — пустая строка (показывать нечего).
export function formatElapsedSince(startIso: string, now: Date = new Date()): string {
  const start = new Date(startIso).getTime()
  if (Number.isNaN(start)) return ''
  return formatDurationMs(Math.max(0, now.getTime() - start))
}

function formatDurationMs(ms: number): string {
  if (ms < MS_MINUTE) return 'меньше минуты'
  const hours = Math.floor(ms / MS_HOUR)
  const minutes = Math.floor((ms % MS_HOUR) / MS_MINUTE)
  if (hours === 0) return `${minutes} мин`
  return `${hours} ч ${String(minutes).padStart(2, '0')} мин`
}

// --- ввод времени (datetime-local) ---
//
// Значение <input type="datetime-local"> — настенное время без зоны, то есть
// намерение человека, а не момент. Моментом его делает зона того, кто набрал, —
// зона его устройства. Прочитать «15:00» зоной организации значит записать чужой
// момент: набранное в Челябинске уедет на два часа и коллеге в Москве покажется
// челябинскими цифрами.

const WALL_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/

// Настенное время («2026-08-08T14:30») → момент (RFC3339 в UTC). Смещение
// берётся у самой зоны в окрестности этого момента; второй проход нужен для
// суток перевода стрелок, где смещение до и после разное. Границу календарных
// суток считают этой же функцией — но зоной организации, переданной явно.
export function wallTimeToIso(wall: string, tz: string = momentZone()): string | undefined {
  const m = WALL_PATTERN.exec(wall)
  if (!m) return undefined
  const asUtc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]))
  const guess = asUtc - zoneOffsetMinutes(new Date(asUtc), tz) * MS_MINUTE
  const instant = asUtc - zoneOffsetMinutes(new Date(guess), tz) * MS_MINUTE
  const d = new Date(instant)
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString()
}

// Момент → значение поля datetime-local в зоне вводящего («2026-08-08T14:30»).
export function isoToWallTime(iso: string, tz: string = momentZone()): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const p = zoneParts(d, tz)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`
}
